import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Grid, Theme, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery} from '@mui/material';

import {getContentStyles, getTabStyles, styles} from './AccountSettings.style';
import {CompanyDepartmentsTab} from './components/CompanyDepartmentsTab/CompanyDepartmentsTab';
import {CompanySettingsTab} from './components/CompanySettingsTab/CompanySettingsTab';
import {PersonalSettingsTab} from './components/PersonalSettingsTab/PersonalSettingsTab';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';

enum SettingsTabs {
  COMPANY_SETTINGS = 'company_settings',
  DEPARTMENTS = 'departments',
  PERSONAL_SETTINGS = 'personal_settings',
}

export const AccountSettings = () => {
  const {t} = useTranslation();
  const {currentUser, isLoading} = useContext(UserContext);
  const [activeTab, setActiveTab] = useState<SettingsTabs>(SettingsTabs.COMPANY_SETTINGS);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLanguage = useContext(LanguageContext);
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  useEffect(() => {
    const urlLang = searchParams.get('lang');

    if (urlLang !== currentLanguage) {
      setSearchParams(
        (prev) => {
          prev.set('lang', currentLanguage);
          return prev;
        },
        {replace: true}
      );
    }
  }, [currentLanguage, searchParams, setSearchParams]);

  const isApplicant = currentUser?.role.type === 'applicant';

  useEffect(() => {
    if (isApplicant) {
      setActiveTab(SettingsTabs.PERSONAL_SETTINGS);
    } else {
      setActiveTab(SettingsTabs.COMPANY_SETTINGS);
    }
  }, [isApplicant]);

  if (!currentUser || isLoading) {
    return null;
  }

  return (
    <Grid container>
      <Grid item xs={12} sx={{marginBottom: '32px'}}>
        <Typography variant='h1' display={'flex'} gap={1}>
          {t('settings.title')}
        </Typography>
      </Grid>

      {!isApplicant && (
        <Grid item xs={12}>
          <ToggleButtonGroup
            fullWidth
            exclusive
            value={activeTab}
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setActiveTab(newValue);
              }
            }}
            sx={styles.buttonContainer}
          >
            <ToggleButton
              value={SettingsTabs.COMPANY_SETTINGS}
              sx={getTabStyles(activeTab === SettingsTabs.COMPANY_SETTINGS)}
            >
              <Typography variant={isXs ? 'h4' : 'h3'}>
                {t('appHeader.menuLabels.settingsTabs.companySettings')}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value={SettingsTabs.DEPARTMENTS}
              sx={getTabStyles(activeTab === SettingsTabs.DEPARTMENTS)}
            >
              <Typography variant={isXs ? 'h4' : 'h3'}>
                {t('appHeader.menuLabels.settingsTabs.departments')}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value={SettingsTabs.PERSONAL_SETTINGS}
              sx={getTabStyles(activeTab === SettingsTabs.PERSONAL_SETTINGS)}
            >
              <Typography variant={isXs ? 'h4' : 'h3'}>
                {t('appHeader.menuLabels.settingsTabs.userSettings')}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}

      <Grid item xs={12} sx={getContentStyles()}>
        {activeTab === SettingsTabs.COMPANY_SETTINGS && !isApplicant && <CompanySettingsTab />}
        {activeTab === SettingsTabs.DEPARTMENTS && !isApplicant && <CompanyDepartmentsTab />}
        {activeTab === SettingsTabs.PERSONAL_SETTINGS && <PersonalSettingsTab />}
      </Grid>
    </Grid>
  );
};
